import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, UserCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import { notificationsApi } from '../../services/api'
import { dispatchNotificationsChanged } from '../../hooks/useMarkNotificationsRead'
import { formatDateTime } from '../../utils/date'

const NOTIFICATION_MODULE_KEYS = {
  forms_applications: 'admin.forms.applications',
  forms_counseling: 'admin.forms.counseling',
  forms_contact: 'admin.forms.contact',
  recruitment_applications: 'admin.recruitment.applications',
}

function notificationModuleLabel(module, t) {
  const key = NOTIFICATION_MODULE_KEYS[module]
  return key ? t(key) : module
}

function AdminNotifications() {
  const { t } = useTranslation()
  const { hasModuleAccess } = useAuth()
  const [open, setOpen] = useState(false)
  const [data, setData] = useState({ total: 0, groups: [], recent: [] })
  const panelRef = useRef(null)

  const canViewNotifications =
    hasModuleAccess('forms_applications') ||
    hasModuleAccess('forms_counseling') ||
    hasModuleAccess('forms_contact') ||
    hasModuleAccess('recruitment_applications')

  const load = () => {
    if (!canViewNotifications) return

    notificationsApi
      .getAll()
      .then(({ data: response }) => setData(response.data || response))
      .catch(() => setData({ total: 0, groups: [], recent: [] }))
  }

  useEffect(() => {
    load()
    if (!canViewNotifications) return undefined

    const interval = window.setInterval(load, 60000)
    const handleChanged = () => load()
    window.addEventListener('admin-notifications-changed', handleChanged)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('admin-notifications-changed', handleChanged)
    }
  }, [canViewNotifications])

  const markNotificationRead = (payload) => {
    notificationsApi
      .markRead(payload)
      .then(() => {
        dispatchNotificationsChanged()
        load()
      })
      .catch(() => {})
  }

  const handleGroupClick = (group) => {
    setOpen(false)
    markNotificationRead({ module: group.module })
  }

  const handleItemClick = (item) => {
    setOpen(false)
    markNotificationRead({ module: item.module, item_id: item.id })
  }

  useEffect(() => {
    if (!open) return undefined

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  if (!canViewNotifications) return null

  const total = data.total || 0

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-text/70 transition-colors hover:border-primary/30 hover:text-primary"
        aria-label={t('admin.notifications.title')}
      >
        <Bell className="h-5 w-5" />
        {total > 0 ? (
          <span className="absolute -end-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white">
            {total > 99 ? '99+' : total}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute end-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-white shadow-xl">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold text-primary">{t('admin.notifications.title')}</h2>
            <p className="mt-0.5 text-xs text-text/55">{t('admin.notifications.subtitle')}</p>
          </div>

          {total === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-text/50">{t('admin.notifications.empty')}</div>
          ) : (
            <>
              {data.groups?.length ? (
                <div className="space-y-1 border-b border-border px-3 py-3">
                  {data.groups.map((group) => (
                    <Link
                      key={group.module}
                      to={group.path}
                      onClick={() => handleGroupClick(group)}
                      className="flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors hover:bg-primary/5"
                    >
                      <span className="font-medium text-text">{notificationModuleLabel(group.module, t)}</span>
                      <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
                        {group.count}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : null}

              {data.recent?.length ? (
                <div className="max-h-72 overflow-y-auto px-1 py-2">
                  {data.recent.map((item) => (
                    <Link
                      key={`${item.module}-${item.id}`}
                      to={item.path}
                      onClick={() => handleItemClick(item)}
                      className="block rounded-xl px-3 py-2.5 transition-colors hover:bg-background"
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-primary/70">
                        {notificationModuleLabel(item.module, t)}
                      </div>
                      <div className="mt-0.5 text-sm font-medium text-text">{item.title || '—'}</div>
                      {item.subtitle ? <div className="text-xs text-text/60">{item.subtitle}</div> : null}
                      {item.detail ? <div className="text-xs text-text/50">{item.detail}</div> : null}
                      {item.created_at ? (
                        <div className="mt-1 text-[11px] text-text/45">{formatDateTime(item.created_at)}</div>
                      ) : null}
                    </Link>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default function AdminTopBar() {
  const { t } = useTranslation()
  const { user } = useAuth()

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-end gap-2 border-b border-border bg-background/95 px-8 backdrop-blur">
      <AdminNotifications />
      <Link
        to="/cp-login/profile"
        className="flex h-10 max-w-[220px] items-center gap-2 rounded-full border border-border bg-white px-3 text-sm font-medium text-text/80 transition-colors hover:border-primary/30 hover:text-primary"
        title={t('admin.profile.title')}
      >
        <UserCircle className="h-5 w-5 shrink-0 text-primary" />
        <span className="truncate">{user?.name || user?.username || t('admin.profile.title')}</span>
      </Link>
    </header>
  )
}
